import React, { useCallback } from 'react';
import { Outlet, useRoutes } from 'react-router';
import { LinkProps, NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { FormsSearch } from './forms-search';
import { RulesSearch } from './rules-search';
import { StatutesSearch } from './statutes-search';
import { NewsSearch } from './news-search';
import { CasesSearch } from './cases-search';
import { SecondarySourcesSearch } from './secondary-sources-search';
import { PractitionerSearch } from './practitioner-search';
import { useAppendQueryString } from './useAppendQueryString';
import { RedirectSearch } from './RedirectSearch';
import { searchHeaderAtom } from './SearchFilters';
import { CoursesSearch } from './courses-search';
import { useAtom } from 'jotai';
import { useAccessibleNavItems, useActiveNav } from 'features/NavGroup';
import { ClearSearchButton } from './ClearSearchButton';
import { StyledCenteredContent } from 'features/layout/content/StyledCenteredContent';
import { StyleConstant } from 'util/getStyleConstant';

export const searchRoutes = [
  {
    Component: SearchLayout,
    children: [
      {
        index: true,
        element: <RedirectSearch />,
      },
      {
        path: '/practitioner',
        element: <PractitionerSearch />,
      },
      {
        path: '/secondary-sources',
        element: <SecondarySourcesSearch />,
      },
      {
        path: '/cases',
        element: <CasesSearch />,
      },
      {
        path: '/news',
        element: <NewsSearch />,
      },
      {
        path: '/statutes',
        element: <StatutesSearch />,
      },
      {
        path: '/rules',
        element: <RulesSearch />,
      },
      {
        path: '/documents_and_forms',
        element: <FormsSearch />,
      },
      {
        path: '/courses',
        element: <CoursesSearch />,
      },
    ],
  },
];

function SearchSectionLink({
  to,
  children,
}: Pick<LinkProps, 'to' | 'children'>) {
  const appendQueryString = useAppendQueryString();
  return (
    <li>
      <NavLink
        to={appendQueryString(to as string, ['query'])}
        className={({ isActive }) => {
          return isActive ? 'active' : undefined;
        }}
      >
        {children}
      </NavLink>
    </li>
  );
}

export default function Search() {
  return useRoutes(searchRoutes);
}

function SearchLayout() {
  const { searchable } = useAccessibleNavItems();

  return (
    <StyledSearchLayout>
      <StyledCenteredSearchContent>
        <SearchSubNav />
      </StyledCenteredSearchContent>
      <section
        className={`search-content${searchable.length > 1 ? ' has-subnav' : ''}`}
      >
        <StyledCenteredSearchContent>
          <Outlet />
        </StyledCenteredSearchContent>
      </section>
    </StyledSearchLayout>
  );
}

export function SearchSubNav() {
  const [searchHeader, setSearchHeader] = useAtom(searchHeaderAtom);
  const { searchable } = useAccessibleNavItems();
  const item = useActiveNav()?.item;

  const hasFilters = React.useMemo(() => {
    return item && !item.hideSearchFilters;
  }, [item]);

  const handleToggleFilters = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget?.blur();
      setSearchHeader(state => ({ ...state, open: !state.open }));
    },
    [setSearchHeader],
  );

  return (
    <StyledNavSection>
      <ClearSearchButton />
      <ul>
        {searchable.length > 1 &&
          searchable.map((item, i) => {
            return (
              <SearchSectionLink
                to={item.searchPath!}
                key={item.searchPath + '-' + i}
              >
                {item.searchLabel}
              </SearchSectionLink>
            );
          })}
      </ul>
      {hasFilters && (
        <StyledFilterSearchButton onClick={handleToggleFilters}>
          <i className="fa fa-solid fa-filter"></i>
          {searchHeader.open ? 'Hide Filters' : 'Filter Search…'}
        </StyledFilterSearchButton>
      )}
    </StyledNavSection>
  );
}

export const StyledCenteredSearchContent = styled(StyledCenteredContent)`
  max-width: 1080px;
`;

const StyledFilterSearchButton = styled.button`
  font-size: 15px;
  font-weight: bold;
  font-family: var(--FONT_SANS_SERIF);
  color: var(--COLOR_NICE_BLUE);
  margin-top: -10px;
  outline: none;
  text-align: right;
  white-space: nowrap;
  width: 119px;
  &:active {
    outline: none;
  }
  i {
    font-size: 12px;
    margin-right: 7px;
  }
`;

export const StyledSearchLayout = styled.section`
  margin: 0 auto;
  margin-bottom: 90px;
  width: 100%;
  .search-content {
    padding-top: 24px;
  }
  .search-content.has-subnav {
    border-top: 1px solid #e3dfda;
  }
`;

export const StyledNavSection = styled.nav`
  display: flex;
  font-family: var(--FONT_SANS_SERIF);
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  font-weight: 500;
  line-height: 1.29;
  ul {
    display: flex;

    gap: 24px;
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
    overflow: auto;
    @media screen and (min-width: ${StyleConstant.BREAKPOINT_HANDHELD}) {
      margin-left: 50px;
    }
    li {
      display: inline-block;
      white-space: nowrap;
    }
  }
  a {
    display: block;
    padding-bottom: 8px;
    border-bottom: 2px solid transparent;
    color: var(--COLOR_COOL_GRAY);
    &:visited {
      color: var(--COLOR_COOL_GRAY);
    }
    &.active {
      font-weight: 900;
      color: var(--COLOR_DARK_GREY_BLUE);
      border-bottom: 2px solid var(--COLOR_UCLA_GOLD);
    }
  }
`;
